import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useOktaAuth, LoginCallback } from '@okta/okta-react';
import './App.css';
import TranslationForm from './components/TranslationForm';
import BulkUpload from './components/BulkUpload';
import RequestsTable from './components/RequestsTable';
import RequestDetail from './components/RequestDetail';
import BulkUploadHistory from './components/BulkUploadHistory';
import TranslationMemory from './components/TranslationMemory';
// import PhraseDictionary from './components/PhraseDictionary'; // hidden — no longer relevant
import AdminDashboard from './components/AdminDashboard';
import ReviewQueue from './components/ReviewQueue';
import Reports from './components/Reports';
import Loading from './components/Loading';
import { getSingleRequests, submitTranslation, getRequestStatus, saveToTranslationMemory, saveDraftTranslation, setAuthToken, setTokenGetter, lockTranslation, fetchTmConfig } from './api';
import {
  clearDraftFields,
  mergeDraftFieldsFromResponse,
  patchRequestWithDraftFields,
} from './utils/draftState';
import usePendingTranslations from './hooks/usePendingTranslations';
import { applyLockToRequest, isLockedByOther, normalizeLockState, normalizeUserId } from './utils/translationLock';

function MainApp() {
  const { oktaAuth, authState } = useOktaAuth();
  const [activeTab, setActiveTab] = useState('submit');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [message, setMessage] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  // Floating toast (bottom-right) used for "translation ready" notifications
  // raised by the visibility-aware polling hook. Independent of `message` so
  // it does not overwrite the confirmation banner shown right after submit.
  const [toast, setToast] = useState(null);

  const [availableTMs, setAvailableTMs] = useState([]);
  const [selectedTmId, setSelectedTmId] = useState('');
  const [tmLoading, setTmLoading] = useState(true);
  const reviewQueueDraftPatchRef = useRef(null);
  const bulkHistoryDraftPatchRef = useRef(null);

  // Toasts dismiss themselves so the user does not have to chase the X.
  // 12s is long enough to read a 2-sentence message comfortably.
  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(null), 12000);
    return () => clearTimeout(id);
  }, [toast]);

  // The inline message banner (shown above the active tab) should also
  // auto-dismiss for success cases - errors stay until the user reads /
  // dismisses them so they are not missed.
  useEffect(() => {
    if (!message || message.type !== 'success') return undefined;
    const id = setTimeout(() => setMessage(null), 12000);
    return () => clearTimeout(id);
  }, [message]);

  useEffect(() => {
    if (!authState) return;

    if (!authState.isAuthenticated) {
      oktaAuth.signInWithRedirect();
      return;
    }

    // Register a live getter so every API call always uses the current Okta
    // access token.  Okta silently renews the token in the background
    // (offline_access scope); without this the stored snapshot goes stale
    // after ~1 hour and the API Gateway authorizer returns an "explicit deny"
    // error that only resolves on a full page refresh.
    setTokenGetter(() => oktaAuth.getAccessToken());

    // Also seed the static fallback for callers that read getAuthToken() directly.
    const token = oktaAuth.getAccessToken();
    if (token) {
      setAuthToken(token);
    }

    oktaAuth.getUser().then((info) => {
      setUserInfo(info);
    }).catch((err) => {
      console.error('Failed to get user info:', err);
    });

  }, [authState, oktaAuth]);

  useEffect(() => {
    if (!authState?.isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const configs = await fetchTmConfig();
        if (cancelled) return;
        const tms = configs.map((c) => ({
          tm_id: c.tm_id,
          label: c.display_name || c.tm_id,
          program: c.program || 'domestic',
          sourceLanguage: c.sourceLanguage || 'en-US',
          targetLanguage: c.targetLanguage || 'es-US',
          category: c.category || '',
          sort_order: c.sort_order ?? 999,
        }));
        setAvailableTMs(tms);
        if (tms.length > 0 && !tms.find((t) => t.tm_id === selectedTmId)) {
          setSelectedTmId(tms[0].tm_id);
        }
      } catch (err) {
        console.error('Failed to load TM config:', err);
      } finally {
        if (!cancelled) setTmLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authState]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSingleRequests();
      setRequests(data);
      setLastRefresh(new Date());
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load requests: ' + error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch the request list whenever the user lands on the My Translations
    // - Sections tab. The Submit tab no longer renders a grid, so it does
    // not need to fetch.
    if (activeTab === 'my-sections' && authState?.isAuthenticated) {
      fetchRequests();
    }
  }, [activeTab, fetchRequests, authState]);

  // Filter the global request list down to the signed-in user's own
  // submissions, matched on the `submitted_by` field (email).
  const myEmail = (userInfo?.email || '').toLowerCase();
  const myRequests = useMemo(() => {
    if (!myEmail) return requests;
    return requests.filter(
      (r) => (r.submitted_by || '').toLowerCase() === myEmail
    );
  }, [requests, myEmail]);

  // Visibility-aware polling for translations submitted in this session.
  // When a tracked req_id transitions to a terminal state we raise a toast
  // so the linguist does not have to babysit the page.
  const handleTranslationReady = useCallback(
    ({ reqId, source }) => {
      const preview = source && source.length > 60 ? source.slice(0, 60) + '…' : source;
      setToast({
        type: 'success',
        text: preview
          ? `Translation ready: "${preview}". Open it from My Translations — Sections to review.`
          : `Translation ready (request ${reqId.slice(0, 8)}…). Open it from My Translations — Sections to review.`,
        reqId,
      });
      // Refresh the embedded grid so the user sees the new state without
      // a manual refresh.
      fetchRequests();
    },
    [fetchRequests]
  );

  const handleTranslationError = useCallback(({ reqId, error_message }) => {
    setToast({
      type: 'error',
      text: error_message
        ? `Translation failed (${reqId.slice(0, 8)}…): ${error_message}`
        : `Translation failed for request ${reqId.slice(0, 8)}…. Please retry.`,
      reqId,
    });
    fetchRequests();
  }, [fetchRequests]);

  const { trackSubmission } = usePendingTranslations({
    getStatus: getRequestStatus,
    onReady: handleTranslationReady,
    onError: handleTranslationError,
  });

  const handleSubmit = async (formData) => {
    setLoading(true);
    setMessage(null);
    try {
      const result = await submitTranslation(formData);
      setMessage({
        type: 'success',
        text: (
          <>
            Translation submitted. Processing now... Request ID {result.req_id}.
            <br />
            You can review the result in <strong>My Translations — Sections</strong> once it's ready.
          </>
        ),
      });
      // Track this submission for visibility-aware polling so the user
      // gets a toast as soon as it's ready, without manually refreshing.
      trackSubmission(result.req_id, formData.source);
      // Refresh the embedded grid so the new request appears immediately.
      fetchRequests();
      return result;
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to submit: ' + error.message });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchRequests();
  };

  const handleViewRequest = async (request) => {
    const cached = requests.find((r) => r.req_id === request.req_id) || request;
    try {
      const updated = await getRequestStatus(request.req_id);
      const merged = updated?.draftTarget
        ? updated
        : patchRequestWithDraftFields(updated, cached);
      setSelectedRequest(merged);
    } catch (error) {
      setSelectedRequest(cached);
    }
  };

  const handleLockStatusChange = useCallback((reqId, lock) => {
    setRequests((prev) => prev.map((r) => (
      r.req_id === reqId ? applyLockToRequest(r, lock) : r
    )));
    setSelectedRequest((prev) => (
      prev?.req_id === reqId ? applyLockToRequest(prev, lock) : prev
    ));
  }, []);

  const handleLockNotify = useCallback(({ type, text }) => {
    setToast({ type: type || 'error', text });
  }, []);

  const selectedLock = selectedRequest ? normalizeLockState(selectedRequest) : null;
  const selectedReadOnly = selectedLock
    ? isLockedByOther(selectedLock, myEmail)
    : false;

  const handleSaveDraft = useCallback(async (reqId, data) => {
    const response = await saveDraftTranslation({ req_id: reqId, ...data });
    const draftFields = mergeDraftFieldsFromResponse(response);
    const patchRow = (row) => (
      row && row.req_id === reqId ? patchRequestWithDraftFields(row, draftFields) : row
    );
    setSelectedRequest((prev) => patchRow(prev));
    setRequests((prev) => prev.map(patchRow));
    reviewQueueDraftPatchRef.current?.(reqId, draftFields);
    bulkHistoryDraftPatchRef.current?.(reqId, draftFields);
    return response;
  }, []);

  const handleSaveTranslation = async (reqId, data, options = {}) => {
    try {
      await saveToTranslationMemory(data);
      if (options.keepOpen) {
        setSelectedRequest((prev) => (
          prev && prev.req_id === reqId
            ? { ...prev, ...clearDraftFields() }
            : prev
        ));
      }
      if (!options.keepOpen) {
        setMessage({
          type: 'success',
          text: (
            <>
              Translation approved and saved to <strong>Translation Memory</strong>.
            </>
          ),
        });
        setSelectedRequest(null);
        fetchRequests();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save: ' + error.message });
      throw error;
    }
  };

  const handleSaveAndLock = useCallback(async (reqId, { resourceType, parentId } = {}) => {
    try {
      const result = await lockTranslation({
        reqId,
        resourceType,
        parentId,
      });
      if (result.warning) {
        setToast({ type: 'error', text: result.warning });
      } else {
        setMessage({
          type: 'success',
          text: (
            <>
              Translation approved, saved to <strong>Translation Memory</strong>, and locked.
            </>
          ),
        });
      }
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      setToast({
        type: 'error',
        text: error.message || 'Saved to TM but lock failed. Try locking from the table.',
      });
      setSelectedRequest(null);
      fetchRequests();
      throw error;
    }
  }, [fetchRequests]);

  const handleLockWarning = useCallback((text) => {
    setToast({ type: 'error', text });
  }, []);

  const handleCloseModal = () => {
    setSelectedRequest(null);
  };

  if (!authState || !authState.isAuthenticated) {
    return <Loading message="Redirecting to login..." />;
  }

  if (!userInfo) {
    return <Loading message="Loading user info..." />;
  }

  if (tmLoading) {
    return <Loading message="Loading translation memories..." />;
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <div className="header-logo">
            <div className="logo-icon">
              <div className="logo-icon-inner">
                <span>C</span>
              </div>
            </div>
            <div className="logo-text">
              <span className="logo-brand">CIGNA</span>
              <span className="logo-subtitle">HEALTH SERVICES</span>
            </div>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span className="user-name">{userInfo?.name || userInfo?.email || 'User'}</span>
              {/* Sign Out button removed per Fernanda's May 2 review:
                  Okta SSO already manages the session, and an explicit
                  Sign Out caused confusion since users assumed it would
                  also drop them out of every other Cigna app. */}
            </div>
          </div>
        </div>
      </header>

      <main className="main-container">
        <div className="main-content">
        <div className="tabs">
          <button
            className={`tab-button ${activeTab === 'submit' ? 'active' : ''}`}
            onClick={() => setActiveTab('submit')}
          >
            Translate Text by Sections
          </button>
          <button
            className={`tab-button ${activeTab === 'my-sections' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-sections')}
          >
            My Translations - Sections
          </button>
          <button
            className={`tab-button ${activeTab === 'bulk' ? 'active' : ''}`}
            onClick={() => setActiveTab('bulk')}
          >
            Translate Files
          </button>
          <button
            className={`tab-button ${activeTab === 'bulk-history' ? 'active' : ''}`}
            onClick={() => setActiveTab('bulk-history')}
          >
            My Translations - Files
          </button>
          <button
            className={`tab-button ${activeTab === 'review-queue' ? 'active' : ''}`}
            onClick={() => setActiveTab('review-queue')}
          >
            Review Translations
          </button>
          <button
            className={`tab-button ${activeTab === 'tm' ? 'active' : ''}`}
            onClick={() => setActiveTab('tm')}
          >
            Translation Memory
          </button>
          {/* Phrase Dictionary tab hidden — no longer relevant
          <button
            className={`tab-button ${activeTab === 'pd' ? 'active' : ''}`}
            onClick={() => setActiveTab('pd')}
          >
            Phrase Dictionary
          </button>
          */}
          <button
            className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            Reports
          </button>
          <button
            className={`tab-button ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            Admin
          </button>
        </div>

        {message && (
          <div className={`message message-${message.type}`}>
            {/* Wrap the body in a single span so the flex layout
                treats it as one item. Without this, mixed text and
                <strong> children each become flex items and pick up
                the parent's `gap`, which causes the bold tab name
                to sit on its own line with phantom whitespace. */}
            <span className="message-text">{message.text}</span>
            <button
              type="button"
              className="message-dismiss"
              aria-label="Dismiss message"
              onClick={() => setMessage(null)}
            >
              ✕
            </button>
          </div>
        )}

        {activeTab === 'submit' && (
          <TranslationForm
            onSubmit={handleSubmit}
            loading={loading}
            selectedTmId={selectedTmId}
            availableTMs={availableTMs}
            onTmChange={setSelectedTmId}
          />
        )}

        {activeTab === 'my-sections' && (
          <RequestsTable
            requests={myRequests}
            loading={loading}
            onRefresh={handleRefresh}
            onViewRequest={handleViewRequest}
            lastRefresh={lastRefresh}
            availableTMs={availableTMs}
            currentUserId={normalizeUserId(userInfo?.email)}
            onLockStatusChange={handleLockStatusChange}
            onNotify={handleLockNotify}
            title="My Translations - Sections"
            subtitle="Saved to your account and available after you sign back in"
            headerHelper="Select Review Translation to review the translation before approving."
            emptyMessage="No translations yet."
            emptyHint="Submit a translation in the Translate Text by Section tab to see it appear here."
          />
        )}

        {activeTab === 'bulk' && (
          <BulkUpload
            selectedTmId={selectedTmId}
            availableTMs={availableTMs}
            onTmChange={setSelectedTmId}
          />
        )}

        {activeTab === 'bulk-history' && (
          <BulkUploadHistory
            availableTMs={availableTMs}
            draftListPatchRef={bulkHistoryDraftPatchRef}
          />
        )}

        {activeTab === 'review-queue' && (
          <ReviewQueue
            onViewRequest={handleViewRequest}
            availableTMs={availableTMs}
            currentUserId={normalizeUserId(userInfo?.email)}
            draftListPatchRef={reviewQueueDraftPatchRef}
            onLockStatusChange={(reqId, lock) => {
              setRequests((prev) => prev.map((r) =>
                r.req_id === reqId ? applyLockToRequest(r, lock) : r
              ));
            }}
            onNotify={setMessage}
          />
        )}

        {activeTab === 'tm' && (
          <TranslationMemory selectedTmId={selectedTmId} availableTMs={availableTMs} onTmChange={setSelectedTmId} />
        )}

        {/* Phrase Dictionary hidden — no longer relevant
        {activeTab === 'pd' && (
          <PhraseDictionary />
        )}
        */}

        {activeTab === 'reports' && (
          <Reports availableTMs={availableTMs} />
        )}

        {activeTab === 'admin' && (
          <AdminDashboard availableTMs={availableTMs} />
        )}

        {selectedRequest && (
          <RequestDetail
            key={`${selectedRequest.req_id}-${selectedRequest.draftEditedAt || 'none'}`}
            request={selectedRequest}
            onClose={handleCloseModal}
            onSave={handleSaveTranslation}
            onSaveDraft={handleSaveDraft}
            onSaveAndLock={handleSaveAndLock}
            onNotifyLockWarning={handleLockWarning}
            availableTMs={availableTMs}
            readOnly={selectedReadOnly}
            lockInfo={selectedLock}
            resourceType="section"
          />
        )}
        </div>
      </main>

      <footer className="app-disclaimer" role="contentinfo">
        <strong>Disclaimer:</strong> This content has been generated using GenAI.
        The AI-generated output should be reviewed by the user to ensure that the
        information is accurate and suitable for intended purpose.
      </footer>

      {toast && (
        <div className={`toast toast-${toast.type}`} role="status" aria-live="polite">
          <span className="toast-icon" aria-hidden="true">
            {toast.type === 'error' ? '!' : '✓'}
          </span>
          <span className="toast-text">{toast.text}</span>
          <button
            className="toast-close"
            onClick={() => setToast(null)}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/login/callback" element={<LoginCallback />} />
      <Route path="/*" element={<MainApp />} />
    </Routes>
  );
}

export default App;
